import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const {
    language,
    setLanguage,
    t
  } = useLanguage();
  const reducedMotion = useReducedMotion();
  const lastScrollY = useRef(0);
  // Intelligent sticky behavior: hide on scroll down, reveal on scroll up.
  useEffect(() => {
    const handleScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 20);
      // Always reveal when the menu is open or near the top of the page.
      if (mobileMenuOpen || y < 80) {
        setHidden(false);
      } else if (y > lastScrollY.current + 6) {
        setHidden(true);
      } else if (y < lastScrollY.current - 6) {
        setHidden(false);
      }
      lastScrollY.current = y;
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [mobileMenuOpen]);
  // Reveal the bar whenever the mobile menu opens.
  useEffect(() => {
    if (mobileMenuOpen) setHidden(false);
  }, [mobileMenuOpen]);
  // Footer protection: hide the sticky bar while the footer is in view so it
  // never overlaps footer links or social icons.
  useEffect(() => {
    const footer = document.querySelector('footer');
    if (!footer) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !mobileMenuOpen) setHidden(true);
    }, {
      threshold: 0.01
    });
    observer.observe(footer);
    return () => observer.disconnect();
  }, [mobileMenuOpen]);
  // Lock background scroll while the mobile menu is open.
  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);
  const navLinks = t('nav.links') as string[];
  return <motion.header initial={{
    y: -100
  }} animate={{
    y: hidden ? -120 : 0
  }} transition={reducedMotion ? {
    duration: 0
  } : {
    duration: 0.24,
    ease: [0.16, 1, 0.3, 1]
  }} className={`fixed top-0 left-0 right-0 z-40 transition-[padding] duration-300 ${scrolled ? 'py-2.5 md:py-4' : 'py-3 md:py-6'}`} data-id="element-211" data-mp="2wbngg5uk5hsn">
      <div className="max-w-7xl mx-auto px-4 sm:px-6" data-id="element-212" data-mp="2sb4ng8gxi5ov">
        <div className={`glass-panel rounded-full pl-4 pr-3 sm:px-6 flex items-center justify-between transition-colors duration-300 h-[60px] md:h-auto md:py-3 ${scrolled ? 'bg-surface/80' : 'bg-surface/40'}`} data-id="element-213" data-mp="2s8ieq5iaxk52">
          {/* Brand lockup — raw transparent PNG, no wrapper/background/effects */}
          <a href="#" aria-label="Blinkblis" className="inline-flex items-center gap-2.5 sm:gap-3 bg-transparent p-0 border-0 shadow-none cursor-pointer group shrink-0 rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/60" data-id="element-214" data-mp="2nm0uxz9xhts">
            <img src="https://cdn.magicpatterns.com/uploads/w8wf8HrNFmFRNJWrke4AoT/blinkblis-navbar-mark-transparent-512.png" alt="Blinkblis" className="block h-11 w-11 md:h-12 md:w-12 shrink-0 object-contain bg-transparent p-0 m-0 border-0 shadow-none rounded-none" style={{
            background: 'transparent',
            backgroundColor: 'transparent',
            padding: 0,
            border: 0,
            boxShadow: 'none',
            filter: 'none',
            borderRadius: 0
          }} data-id="element-215" data-mp="16gmzmb99rcp" />
            <span className="block text-white text-[22px] sm:text-[22px] font-semibold tracking-[-0.02em] leading-none group-hover:text-white/90 transition-colors" data-id="element-216" data-mp="1gkuqgf4ugo1d">
              Blinkblis
            </span>
          </a>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-7 mx-6" data-id="element-217" data-mp="3k19cfscckx2r">
            {navLinks.map(link => <a key={link} href={`#${link.toLowerCase()}`} className="text-sm text-white/70 hover:text-white transition-colors whitespace-nowrap rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/60" data-id="element-218" data-mp="3f414ekkwy9f8">
                {link}
              </a>)}
          </nav>

          {/* Right Actions */}
          <div className="hidden md:flex items-center gap-4 shrink-0" data-id="element-219" data-mp="1vobhc3ms8uy">
            <div className="flex items-center gap-1 text-xs font-medium text-white/50 bg-white/5 p-1 rounded-full border border-white/10" data-id="element-220" data-mp="3u1tf8zfim9n3">
              <button onClick={() => setLanguage('en')} className={`px-2.5 py-1 rounded-full transition-all duration-300 active:scale-[0.95] focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/60 ${language === 'en' ? 'bg-white/10 text-white shadow-[0_2px_8px_rgba(0,0,0,0.2)]' : 'hover:text-white hover:bg-white/5'}`} data-id="element-221" data-mp="uetmz7ilz7w1">
                EN
              </button>
              <button onClick={() => setLanguage('es')} className={`px-2.5 py-1 rounded-full transition-all duration-300 active:scale-[0.95] focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/60 ${language === 'es' ? 'bg-white/10 text-white shadow-[0_2px_8px_rgba(0,0,0,0.2)]' : 'hover:text-white hover:bg-white/5'}`} data-id="element-222" data-mp="2m9bc7ohxnk60">
                ES
              </button>
            </div>
            <button className="bg-white text-background px-5 py-2.5 rounded-full text-sm font-medium hover:bg-white/90 hover:-translate-y-0.5 hover:shadow-[0_4px_12px_rgba(255,255,255,0.1)] active:scale-[0.98] transition-all duration-300 whitespace-nowrap shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/60" data-id="element-223" data-mp="34yrgmmwqnwrp">
              {t('nav.startExploring')}
            </button>
          </div>

          {/* Mobile Toggle */}
          <button className="md:hidden text-white/80 hover:text-white w-11 h-11 flex items-center justify-center rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/60 active:bg-white/5 transition-colors" aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'} aria-expanded={mobileMenuOpen} onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-id="element-224" data-mp="19ute53cmlsnt">
            {mobileMenuOpen ? <X className="w-6 h-6" data-id="element-225" data-mp="27j3ktvncx4l9" /> : <Menu className="w-6 h-6" data-id="element-226" data-mp="22ll7lt2b437g" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence data-id="element-227" data-mp="d7gdj9na3bnz">
        {mobileMenuOpen && <motion.div initial={{
        opacity: 0,
        y: -8
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -8
      }} transition={reducedMotion ? {
        duration: 0
      } : {
        duration: 0.22
      }} className="md:hidden absolute top-full left-0 right-0 mt-2 px-4 max-h-[calc(100dvh-90px)] overflow-y-auto" data-id="element-228" data-mp="d888ci91x5d7">
            <div className="relative rounded-3xl p-6 flex flex-col gap-6 bg-background/85 backdrop-blur-[14px] border border-white/10 overflow-hidden" data-id="element-229" data-mp="262zdqc9bneg3">
              {/* Very soft orange ambient light only — no bright fog/bands */}
              <div className="absolute -top-16 -right-10 w-48 h-48 bg-accent/10 rounded-full blur-3xl pointer-events-none" data-id="element-230" data-mp="1w5ur6bbkdgyr" />

              <nav className="relative flex flex-col gap-7" data-id="element-231" data-mp="3lhwwsf538nfm">
                {navLinks.map(link => <a key={link} href={`#${link.toLowerCase()}`} onClick={() => setMobileMenuOpen(false)} className="text-[23px] font-display font-medium text-white/85 hover:text-white transition-colors leading-none min-h-[44px] flex items-center rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/60" data-id="element-232" data-mp="3qh00nw9c0nm0">
                    {link}
                  </a>)}
              </nav>

              <div className="relative h-px bg-white/10" data-id="element-233" data-mp="1qk69k50f4eq8" />

              <div className="relative flex flex-col gap-5" data-id="element-234" data-mp="1qilgkjtr53uh">
                <div className="flex items-center gap-1 text-sm font-medium text-white/50 bg-white/5 p-1 rounded-full border border-white/10 w-fit" data-id="element-235" data-mp="1lkjxw008n6jj">
                  <button onClick={() => setLanguage('en')} className={`px-5 py-2 rounded-full transition-all duration-300 active:scale-[0.95] focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/60 ${language === 'en' ? 'bg-white/10 text-white shadow-[0_2px_8px_rgba(0,0,0,0.2)]' : 'hover:text-white hover:bg-white/5'}`} data-id="element-236" data-mp="1gnth1agm06x2">
                    EN
                  </button>
                  <button onClick={() => setLanguage('es')} className={`px-5 py-2 rounded-full transition-all duration-300 active:scale-[0.95] focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/60 ${language === 'es' ? 'bg-white/10 text-white shadow-[0_2px_8px_rgba(0,0,0,0.2)]' : 'hover:text-white hover:bg-white/5'}`} data-id="element-237" data-mp="3s8snzi4xkye1">
                    ES
                  </button>
                </div>
                <button className="w-full bg-white text-background px-5 h-[52px] rounded-full text-[16px] font-medium active:scale-[0.98] transition-transform focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/60" data-id="element-238" data-mp="39l77m6wp0u0n">
                  {t('nav.startExploring')}
                </button>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </motion.header>;
}